import * as React from 'react';
// import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Autocomplete, Box, TextField } from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import {
    DataGrid,
    GridRowModes,
    useGridApiRef,
    GridEditInputCell,
    GridRowEditStopReasons,
    useGridApiContext,
} from '@mui/x-data-grid';
import { useSnackbar } from 'notistack';

// project import
import { useLocalStorage } from 'hooks/useStorage';
import withLoader from 'hoc/witLoader';
import IconButton from '@mui/material/IconButton';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import LastPageIcon from '@mui/icons-material/LastPage';
import {
    getBackgroundColor,
    getHoverBackgroundColor,
    getSelectedBackgroundColor,
    getSelectedHoverBackgroundColor,
} from 'utils/helpers/SetBackgroundColorRow';

function TablePaginationActions(props) {
    const { count, page, rowsPerPage, onPageChange } = props;

    const handleFirstPageButtonClick = event => {
        onPageChange(event, 0);
    };

    const handleBackButtonClick = event => {
        onPageChange(event, page - 1);
    };

    const handleNextButtonClick = event => {
        onPageChange(event, page + 1);
    };

    const handleLastPageButtonClick = event => {
        onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
    };

    return (
        <Box sx={{ flexShrink: 0, ml: 2.5 }}>
            <IconButton
                onClick={handleFirstPageButtonClick}
                disabled={page === 0}
                aria-label="first page"
            >
                <FirstPageIcon />
            </IconButton>
            <IconButton
                onClick={handleBackButtonClick}
                disabled={page === 0}
                aria-label="previous page"
            >
                <KeyboardArrowLeft />
            </IconButton>
            <IconButton
                onClick={handleNextButtonClick}
                disabled={page >= Math.ceil(count / rowsPerPage) - 1}
                aria-label="next page"
            >
                <KeyboardArrowRight />
            </IconButton>
            <IconButton
                onClick={handleLastPageButtonClick}
                disabled={page >= Math.ceil(count / rowsPerPage) - 1}
                aria-label="last page"
            >
                <LastPageIcon />
            </IconButton>
        </Box>
    );
}
TablePaginationActions.propTypes = {
    count: PropTypes.number.isRequired,
    page: PropTypes.number.isRequired,
    rowsPerPage: PropTypes.number.isRequired,
    onPageChange: PropTypes.func.isRequired,
};

const StyledBox = styled(Box)(({ theme }) => ({
    flexGrow: 1,
    width: '100%',
    '& .MuiDataGrid-cell': {
        fontSize: '0.75rem',
        padding: 4,
        borderColor: theme.palette.divider,
        minWidth: 'min-content;',
        maxWidth: 'max-content;',
        '& .MuiInputBase-root': {
            height: '100%',
        },
    },
    '& .MuiDataGrid-editInputCell input': {
        padding: '0 2px',
    },
    '& .MuiDataGrid-cell--editing': {
        backgroundColor: alpha(theme.palette.warning.main, 0.19),
        color: theme.palette.primary.dark,
        '& .MuiInputBase-root': {
            height: '100%',
        },
    },
    '& .MuiDataGrid-columnHeaders': {
        fontSize: '0.8rem',
        top: '24px',
        height: '36px !important',
        minHeight: '36px !important',
        maxHeight: '36px !important',
        lineHeight: '36px !important',
    },

    '& .MuiDataGrid-columnHeader': {
        fontSize: '0.8rem',
        height: '36px !important',
    },

    '& .Mui-error': {
        backgroundColor: alpha(
            theme.palette.error.dark,
            theme.palette.mode === 'dark' ? 0 : 0.1,
        ),
        color: theme.palette.error.main,
    },
    '& .super-table-theme--deactivate': {
        backgroundColor: getBackgroundColor(
            theme.palette.warning.main,
            theme.palette.mode,
        ),
        '&:hover': {
            backgroundColor: getHoverBackgroundColor(
                theme.palette.warning.main,
                theme.palette.mode,
            ),
        },
        '&.Mui-selected': {
            backgroundColor: getSelectedBackgroundColor(
                theme.palette.warning.main,
                theme.palette.mode,
            ),
            '&:hover': {
                backgroundColor: getSelectedHoverBackgroundColor(
                    theme.palette.warning.main,
                    theme.palette.mode,
                ),
            },
        },
    },
}));

function AutocompleteEditCell({
    id,
    value,
    field,
    options,
    disableClearable,
    multiple,
    freeSolo,
}) {
    const apiRef = useGridApiContext();
    const handleValueChange = (event, newValue) => {
        event.stopPropagation();
        const isValid = apiRef.current.setEditCellValue({
            id,
            field,
            value: newValue,
        });

        if (isValid) {
            apiRef.current.stopRowEditMode({ id, field });
        }
    };

    return (
        <Autocomplete
            fullWidth
            disableClearable={disableClearable}
            multiple={multiple}
            options={options}
            freeSolo={freeSolo}
            value={value}
            onChange={handleValueChange}
            renderInput={params => <TextField {...params} />}
        />
    );
}

AutocompleteEditCell.propTypes = {
    id: PropTypes.number,
    value: PropTypes.string,
    field: PropTypes.object,
    options: PropTypes.arrayOf(PropTypes.object),
    disableClearable: PropTypes.bool,
    multiple: PropTypes.bool,
    freeSolo: PropTypes.bool,
};

// eslint-disable-next-line no-unused-vars
const WrapperComponent = ({
    status,
    errorComponent,
    loaderComponent,
    wrappedComponent: Component,
    ...restProps
}) => {
    const loading = !(status === 'success' || status === 'error');
    // eslint-disable-next-line no-unused-vars
    const { component: LoaderComponent, props: loaderProps } = loaderComponent;
    // eslint-disable-next-line no-unused-vars
    const { component: ErrorComponent, props: errorProps } = errorComponent;
    const slotsOverride = {
        LoadingOverlay: LoaderComponent,
        ErrorOverlay: ErrorComponent,
    };
    return (
        <Component
            loading={loading}
            components={slotsOverride}
            {...restProps}
        />
    );
};
WrapperComponent.propTypes = {
    status: PropTypes.string,
    errorComponent: PropTypes.object,
    loaderComponent: PropTypes.object,
    wrappedComponent: PropTypes.func,
    restProps: PropTypes.object,
};

const StyledTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: theme.palette.error.main,
        color: theme.palette.error.contrastText,
    },
}));

const NameEditInputCell = props => {
    const { error } = props;
    return (
        <StyledTooltip open={!!error} title={error}>
            <GridEditInputCell {...props} />
        </StyledTooltip>
    );
};
NameEditInputCell.propTypes = {
    error: PropTypes.string,
};

const getTypeComponent = type => {
    switch (type) {
        case 'autocomplete':
            return Autocomplete;
        default:
            return null;
    }
};

// eslint-disable-next-line no-unused-vars
const EditToolbar = ({ commit, setRows, setRowModesModel, emptyColsModel }) => {
    const { enqueueSnackbar } = useSnackbar();

    // eslint-disable-next-line no-unused-vars
    const handleClick = () => {
        const id = Math.random();
        const rowKeys = Object.keys(emptyColsModel);
        setRows(oldRows => {
            return [{ ...emptyColsModel, id, isNew: true }, ...oldRows];
        });
        setRowModesModel(oldModel => ({
            ...oldModel,
            [id]: { mode: GridRowModes.Edit, fieldToFocus: rowKeys[0] },
        }));
    };

    // eslint-disable-next-line no-unused-vars
    const handleCommit = async (posts, actions) => {
        actions.updateProps({ loading: true });
        await commit(posts);
        actions.remove();
        actions.updateProps({ loading: false });
        enqueueSnackbar('Data Saved', { variant: 'success' });
    };
    // const isAddRecordDisabled = Object.keys(rowModesModel).length > 0;

    return (
        <Box
            sx={{
                borderBottom: 1,
                borderColor: 'divider',
                justifyContent: 'flex-end',
                display: 'flex',
                flexDirection: 'column',
                p: 1,
            }}
        >
            <Box
                sx={{
                    justifyContent: 'flex-end',
                    display: 'flex',
                    p: 1,
                }}
            ></Box>
            <Box
                sx={{
                    justifyContent: 'flex-end',
                    display: 'flex',
                    p: 1,
                }}
            ></Box>
        </Box>
    );
};

EditToolbar.propTypes = {
    commit: PropTypes.func,
    setRows: PropTypes.func,
    rowModesModel: PropTypes.object,
    setRowModesModel: PropTypes.func,
    emptyColsModel: PropTypes.object,
};

const StandardDataGrid = ({
    columnsStorageName,
    columns,
    data,
    status,
    entity,
    // execute,
    onRowsUpdate,
    onChangeSearchParams,
    toolbar,
    hideFooterPagination = true,
    ...restProps
}) => {
    // let onExecute = execute;

    const [rows, setRows] = React.useState([]);
    const [rowModesModel, setRowModesModel] = React.useState({});
    const { enqueueSnackbar } = useSnackbar();

    const apiRef = useGridApiRef();

    // eslint-disable-next-line no-unused-vars
    const handleValueChange = (_, newValue) => {
        apiRef.current.setEditCellValue({
            // id,
            // field,
            // // @ts-expect-error i can't figure out how to use AutocompleteValue
            // value: typeof newValue === "string" ? value : newValue?.value || "",
        });
    };

    const gridColumns = columns.map(column => {
        const { hide = false, type = 'string', ...restColumnProps } = column;
        let props = {
            ...restColumnProps,
            filterable: false,
            sortable: false,
            hide,
        };
        const Component = getTypeComponent(type);
        if (Component)
            props = {
                ...props,
                renderEditCell: params => {
                    return (
                        <AutocompleteEditCell
                            freeSolo={true}
                            {...params}
                            {...restColumnProps}
                        />
                    );
                },
            };
        else
            props = {
                ...props,
                type,
            };
        return props;
    });

    // const emptyColsModel = gridColumns.reduce((allCols, col) => {
    //     if (col.editable) allCols[col.field] = '';
    //     return allCols;
    // }, {});

    const defaultColumnsVisibilityModel = gridColumns.reduce((allCols, col) => {
        allCols[col.field] = !col.hide;
        return allCols;
    }, {});
    const [columnVisibilityModel, setColumnVisibilityModel] = useLocalStorage(
        `${columnsStorageName}.columns`,
        defaultColumnsVisibilityModel,
    );

    const processRowUpdate = newRow => {
        const updatedRow = { ...newRow };
        onRowsUpdate(
            rows.map(row => (row.id === newRow.id ? updatedRow : row)),
        );
        // setRows(rows.map(row => (row.id === newRow.id ? updatedRow : row)));
        return updatedRow;
    };

    const handleProcessRowUpdateError = React.useCallback(error => {
        enqueueSnackbar(error.message, { variant: 'error' });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
        setRows(data ?? []);
    }, [data]);

    // React.useEffect(() => {
    // if (location?.state === "refresh")
    // onExecute(searchParams(pagination, filters, sort));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // }, [location?.state, execute]);

    const handleRowEditStop = (params, event) => {
        if (params.reason === GridRowEditStopReasons.rowFocusOut) {
            // event.defaultMuiPrevented = true;
        }
    };

    return (
        <StyledBox>
            <DataGrid
                initialState={{
                    density: 'compact',
                }}
                editMode="row"
                rowModesModel={rowModesModel}
                onRowModesModelChange={newModel => setRowModesModel(newModel)}
                processRowUpdate={processRowUpdate}
                // eslint-disable-next-line no-unused-vars
                onRowEditCommit={(id, event) => console.log(id)}
                onProcessRowUpdateError={handleProcessRowUpdateError}
                onRowEditStop={handleRowEditStop}
                hideFooterPagination={hideFooterPagination}
                experimentalFeatures={{ newEditingApi: true }}
                autoHeight
                hideFooter={true}
                hideFooterSelectedRowCount={true}
                disableColumnSorting={true}
                disableColumnSelector={true}
                rows={rows ?? []}
                columns={gridColumns}
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={newModel =>
                    setColumnVisibilityModel(newModel)
                }
                disableIgnoreModificationsIfProcessingProps={false}
                {...restProps}
                componentsProps={{
                    ...restProps?.componentsProps,
                    // toolbar: {
                    //     ...restProps?.componentsProps?.toolbar,
                    //     setRows,
                    //     rowModesModel,
                    //     setRowModesModel,
                    //     emptyColsModel,
                    // },
                    // pagination: {
                    //     ActionsComponent: TablePaginationActions,
                    // },
                }}
                // components={{
                //     Toolbar: toolbar ? toolbar : EditToolbar,
                // }}
                columnHeaderHeight={36}
                rowHeight={20}
                disableColumnMenu={true}
                // getRowClassName={(params) => {
                //     return (
                //         entity?.ModelFeatureAttribute[
                //             TableActionsTypes.Deactivate
                //         ] &&
                //         params.row.inactive &&
                //         `super-table-theme--deactivate`
                //     );
                // }}
            />
        </StyledBox>
    );
};

StandardDataGrid.propTypes = {
    columns: PropTypes.arrayOf(PropTypes.object),
    columnsStorageName: PropTypes.string,
    toolbar: PropTypes.func,
    hideFooterPagination: PropTypes.bool,
    // execute: PropTypes.func.isRequired,
    entity: PropTypes.func,
    onChangeSearchParams: PropTypes.func,
    // data: PropTypes.array,
    status: PropTypes.string,
};

export default withLoader(StandardDataGrid, {
    WrapperComponent: {
        component: WrapperComponent,
    },
});
